import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { propertiesApi } from "../api/properties";
import type {
	PropertyResponse,
	PropertyPaginated,
	PropertyCreate,
	PropertyUpdate,
} from "../schemas/property";
import { useNavigate } from "react-router";

import { ListParams } from "@/schemas/common";
import { DASHBOARD_QUERY_KEYS } from "@/features/dashboard/hooks/queryKeys";
import { track } from "@/utils/analytics";

export const useProperties = (
	params?: ListParams & { enabled?: boolean; search?: string },
) => {
	const { enabled, ...apiParams } = params || {};
	return useQuery<PropertyPaginated, Error>({
		queryKey: ["properties", apiParams],
		queryFn: () => propertiesApi.getAll(apiParams),
		enabled: enabled ?? true,
	});
};

/**
 * Fetch only active properties (available for mortgage)
 */
export const useActiveProperties = () => {
	return useQuery<PropertyPaginated, Error>({
		queryKey: ["properties", "activa"],
		queryFn: () => propertiesApi.getByEstado("activa"),
	});
};

export const useProperty = (id: number) => {
	return useQuery<PropertyResponse, Error>({
		queryKey: ["properties", id],
		queryFn: () => propertiesApi.getById(id),
		enabled: !!id,
	});
};

export const useCreateProperty = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	return useMutation({
		mutationFn: (data: PropertyCreate) => propertiesApi.create(data),
		onSuccess: (data) => {
			track("property_created", {});
			queryClient.invalidateQueries({ queryKey: ["properties"] });
			queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.all });
			navigate(`/properties/${data.id}`);
		},
	});
};

export const useUpdateProperty = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: number; data: PropertyUpdate }) =>
			propertiesApi.update(id, data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["properties"] });
			queryClient.invalidateQueries({ queryKey: ["properties", data.id] });
			queryClient.invalidateQueries({ queryKey: ["valuations", data.id] });
			queryClient.invalidateQueries({
				queryKey: ["valuations-projection", data.id],
			});
			queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.all });
		},
	});
};

export const useDeleteProperty = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: propertiesApi.delete,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["properties"] });
			queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.all });
		},
	});
};
